// Event Consent App - Clean Architecture
class EventConsentApp {
  constructor() {
    this.currentView = 'loading';
    this.currentStep = 'welcome';
    this.signaturePad = null;
    this.formData = {};
    this.eventData = {};
  }

  init() {
    this.parseURL();
    this.render();
    this.attachEvents();
  }

  parseURL() {
    const params = new URLSearchParams(window.location.search);
    
    if (params.has('qr')) {
      this.currentView = 'qr-generator';
    } else if (params.has('event')) {
      this.currentView = 'consent-form';
      this.eventData = {
        name: params.get('event'),
        location: params.get('location'),
        date: params.get('date')
      };
    } else {
      // No valid params - show error
      this.currentView = 'error';
    }
  }

  render() {
    const app = document.getElementById('ec-app');
    
    if (this.currentView === 'qr-generator') {
      app.innerHTML = this.renderQRGenerator();
    } else if (this.currentView === 'consent-form') {
      app.innerHTML = this.renderConsentForm();
    } else {
      app.innerHTML = this.renderError();
    }
  }

  renderError() {
    return `
      <div id="ec-welcome-section" class="ec-nda-section active ec-animate-fade-in">
        <div class="ec-container">
          <div class="ec-welcome-content">
            <div class="ec-badge-pill">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
              Access Required
            </div>
            
            <h1 class="ec-hero-title">Event Media Consent</h1>
            <p class="ec-hero-subtitle">This consent form must be accessed by scanning a QR code at your event. Please ask event staff for assistance.</p>
            
            <div class="ec-feature-grid">
              <div class="ec-feature-card ec-animate-slide-up">
                <div class="ec-feature-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"></path>
                  </svg>
                </div>
                <h3>Scan QR Code</h3>
                <p>Use your phone camera to scan the QR code displayed at the event</p>
              </div>
              
              <div class="ec-feature-card ec-animate-slide-up">
                <div class="ec-feature-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
                <h3>Fill & Sign</h3>
                <p>Complete your details and provide your digital signature</p>
              </div>
              
              <div class="ec-feature-card ec-animate-slide-up">
                <div class="ec-feature-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3>Done!</h3>
                <p>Your consent is securely submitted and recorded</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderWelcome() {
    return this.renderError();
  }

  renderQRGenerator() {
    return `

      <div class="ec-container">
        <div class="ec-page-title ec-animate-fade-in">
          <div class="ec-page-title-icon">
            <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"></path>
            </svg>
          </div>
          <h1>Event QR Code Generator</h1>
          <p>Generate and download a QR code for attendees to scan and sign the media consent form.</p>
        </div>
        
        <div class="ec-two-column-grid">
          <!-- Settings Card -->
          <div class="ec-card ec-animate-slide-in">
            <h2 class="ec-card-title">Customise QR Code</h2>
            
            <div class="ec-form-grid">
              <div class="ec-form-group">
                <label>Event Name <span class="ec-required">*</span></label>
                <input type="text" id="ec-event-name" class="ec-input" placeholder="e.g., Summer Beauty Workshop" />
              </div>
              
              <div class="ec-form-group">
                <label>Event Location <span class="ec-required">*</span></label>
                <input type="text" id="ec-event-location" class="ec-input" placeholder="e.g., Sydney Convention Centre" />
              </div>
              
              <div class="ec-form-group">
                <label>Event Date <span class="ec-required">*</span></label>
                <input type="date" id="ec-event-date" class="ec-input" />
              </div>
              
              <div class="ec-form-group">
                <label>Consent Form URL</label>
                <div class="ec-url-copy-wrapper">
                  <input type="text" id="ec-consent-url" class="ec-input" readonly />
                  <button type="button" id="ec-copy-btn" class="ec-btn ec-btn-secondary ec-btn-icon" title="Copy URL">
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
              </div>
              
              <button type="button" id="ec-download-btn" class="ec-btn ec-btn-primary">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                Download QR Code
              </button>
              
              <p class="ec-helper-text">QR code updates automatically as you type</p>
            </div>
          </div>
          
          <!-- QR Preview Card -->
          <div class="ec-card ec-animate-scale-in">
            <h2 class="ec-card-title ec-text-center">QR Code Preview</h2>
            
            <div class="ec-qr-display">
              <div class="ec-qr-code-wrapper">
                <div id="ec-qr-code"></div>
              </div>
              
              <div class="ec-qr-info">
                <p class="ec-brand-name">Sachi Skin</p>
                <p class="ec-event-name" id="ec-display-name"></p>
                <p class="ec-event-detail" id="ec-display-location"></p>
                <p class="ec-event-detail" id="ec-display-date"></p>
                <p class="ec-scan-text">Scan to sign consent form</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Instructions -->
        <div class="ec-instructions-card">
          <h3>How to Use</h3>
          <ol class="ec-instructions-list">
            <li>
              <span class="ec-step-number">1</span>
              <span>Download the QR code using the button above</span>
            </li>
            <li>
              <span class="ec-step-number">2</span>
              <span>Print the QR code and display it at your event registration desk</span>
            </li>
            <li>
              <span class="ec-step-number">3</span>
              <span>Attendees scan with their phone camera to open the consent form</span>
            </li>
            <li>
              <span class="ec-step-number">4</span>
              <span>They fill in their details, sign digitally, and submit</span>
            </li>
          </ol>
        </div>
      </div>
      

    `;
  }

  renderConsentForm() {
    if (this.currentStep === 'welcome') {
      return this.renderConsentWelcome();
    } else if (this.currentStep === 'agreement') {
      return this.renderAgreement();
    } else if (this.currentStep === 'signing') {
      return this.renderSigning();
    } else if (this.currentStep === 'success') {
      return this.renderSuccess();
    }
  }

  renderConsentWelcome() {
    return `
      <div id="ec-welcome-section" class="ec-nda-section active ec-animate-fade-in">
        <div class="ec-container">
          <div class="ec-welcome-content">
            <div class="ec-badge-pill">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
              Secure Electronic Signature
            </div>
            
            <h1 class="ec-hero-title">Event Media Consent</h1>
            <p class="ec-hero-subtitle">Welcome to ${this.eventData.name || 'our event'}. Please review and sign our media consent form to complete your registration.</p>
            
            <div class="ec-cta-buttons">
              <button id="ec-btn-start" class="ec-btn ec-btn-primary ec-btn-xl">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                Read & Sign Consent
              </button>
            </div>
            
            <div class="ec-feature-grid">
              <div class="ec-feature-card ec-animate-slide-up">
                <div class="ec-feature-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <h3>Legally Binding</h3>
                <p>Electronic signatures are legally valid and enforceable</p>
              </div>
              
              <div class="ec-feature-card ec-animate-slide-up">
                <div class="ec-feature-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                <h3>Secure Process</h3>
                <p>Your information is encrypted and stored securely</p>
              </div>
              
              <div class="ec-feature-card ec-animate-slide-up">
                <div class="ec-feature-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h3>Quick & Easy</h3>
                <p>Complete the entire process in under 1 minute</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderAgreement() {
    return `
      <div class="ec-container">
        <div class="ec-card">
          <div class="ec-event-banner">
            <h3>${this.eventData.name}</h3>
            <p>${this.eventData.location}</p>
            <p>${this.eventData.date}</p>
          </div>
          
          <h2>Media Consent & Release Agreement</h2>
          <p class="ec-subtitle">Please read the following agreement carefully</p>
          
          <div class="ec-agreement-scroll">
            <p>By signing this Media Consent, Release and Licence Agreement, I acknowledge and agree to the following:</p>
            
            <h4>1. Grant of Rights</h4>
            <p>I grant to Sachi Skin the absolute right to use, reproduce, publish, and distribute photographs, videos, and audio recordings taken at the event.</p>
            
            <h4>2. Permitted Uses</h4>
            <ul>
              <li>Marketing and promotional materials</li>
              <li>Social media posts and advertisements</li>
              <li>Website content and blog posts</li>
              <li>Press releases and media kits</li>
            </ul>
            
            <h4>3. Release and Waiver</h4>
            <p>I release Sachi Skin from any claims arising out of the use of the media.</p>
            
            <h4>4. No Compensation</h4>
            <p>I understand I will not receive financial compensation for the use of the media.</p>
          </div>
          
          <button id="ec-btn-continue" class="ec-btn ec-btn-primary">I Have Read & Agree - Continue</button>
        </div>
      </div>
    `;
  }

  renderSigning() {
    return `
      <div class="ec-container">

        <div class="ec-card">
          <h2>Your Details & Signature</h2>
          <p class="ec-subtitle">Please provide your information and sign below</p>
          
          <form id="ec-consent-form" class="ec-form-grid">
            <div class="ec-form-group">
              <label>Full Name *</label>
              <input type="text" id="ec-full-name" class="ec-input" placeholder="Enter your full name" required />
            </div>
            
            <div class="ec-form-group">
              <label>Email Address *</label>
              <input type="email" id="ec-email" class="ec-input" placeholder="your.email@example.com" required />
            </div>
            
            <div class="ec-form-group">
              <label>Digital Signature *</label>
              <div class="ec-signature-wrapper">
                <canvas id="ec-signature-canvas"></canvas>
                <button type="button" id="ec-clear-btn" class="ec-clear-btn">Clear</button>
              </div>
            </div>
            
            <div class="ec-checkbox-wrapper">
              <input type="checkbox" id="ec-agree" required />
              <label for="ec-agree">I confirm that I have read and agree to the Media Consent & Release Agreement, and that the signature is my own.</label>
            </div>
            
            <div class="ec-form-actions">
              <button type="button" id="ec-btn-back" class="ec-btn ec-btn-secondary">Back</button>
              <button type="submit" id="ec-btn-submit" class="ec-btn ec-btn-primary" disabled>Submit Consent</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  renderSuccess() {
    return `
      <div class="ec-container">
        <div class="ec-logo">
          <h1 class="ec-brand-title">Sachi Skin</h1>
        </div>
        <div class="ec-card ec-text-center">
          <div class="ec-success-icon">✓</div>
          <h2>Thank You!</h2>
          <p>Your consent has been successfully submitted and recorded.</p>
          
          <div class="ec-confirmation-box">
            <h3>Confirmation Details</h3>
            <p><strong>Name:</strong> ${this.formData.fullName}</p>
            <p><strong>Email:</strong> ${this.formData.email}</p>
            <p><strong>Event:</strong> ${this.eventData.name}</p>
            <p><strong>Signature:</strong></p>
            <img src="${this.formData.signature}" alt="Signature" class="ec-signature-img" />
          </div>
        </div>
      </div>
    `;
  }

  attachEvents() {
    if (this.currentView === 'qr-generator') {
      this.attachQREvents();
    } else if (this.currentView === 'consent-form') {
      this.attachConsentEvents();
    }
    // No events needed for error view
  }

  attachQREvents() {
    // Generate initial QR code
    this.updateQR();
    
    // Update QR on input
    ['ec-event-name', 'ec-event-location', 'ec-event-date'].forEach(id => {
      document.getElementById(id)?.addEventListener('input', () => this.updateQR());
    });

    document.getElementById('ec-copy-btn')?.addEventListener('click', () => this.copyURL());
    document.getElementById('ec-download-btn')?.addEventListener('click', () => this.downloadQR());
  }

  attachConsentEvents() {
    if (this.currentStep === 'welcome') {
      document.getElementById('ec-btn-start')?.addEventListener('click', () => {
        this.showAgreementModal();
      });
    } else if (this.currentStep === 'agreement') {
      document.getElementById('ec-btn-continue')?.addEventListener('click', () => {
        this.currentStep = 'signing';
        this.render();
        this.attachEvents();
      });
    } else if (this.currentStep === 'signing') {
      this.initSignaturePad();
      
      document.getElementById('ec-btn-back')?.addEventListener('click', () => {
        this.currentStep = 'agreement';
        this.render();
        this.attachEvents();
      });

      document.getElementById('ec-clear-btn')?.addEventListener('click', () => {
        this.signaturePad?.clear();
        this.validateForm();
      });

      const form = document.getElementById('ec-consent-form');
      form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitConsent();
      });

      ['ec-full-name', 'ec-email', 'ec-agree'].forEach(id => {
        document.getElementById(id)?.addEventListener('input', () => this.validateForm());
      });
    }
  }

  updateQR() {
    const name = document.getElementById('ec-event-name')?.value || '';
    const location = document.getElementById('ec-event-location')?.value || '';
    const date = document.getElementById('ec-event-date')?.value || '';

    const url = `${window.location.origin}${window.location.pathname}?event=${encodeURIComponent(name)}&location=${encodeURIComponent(location)}&date=${date}`;

    document.getElementById('ec-display-name').textContent = name || 'Event Name';
    document.getElementById('ec-display-location').textContent = location || 'Event Location';
    document.getElementById('ec-display-date').textContent = date || 'Event Date';
    document.getElementById('ec-consent-url').value = url;

    const qrDiv = document.getElementById('ec-qr-code');
    qrDiv.innerHTML = '';
    
    if (typeof QRCode !== 'undefined') {
      new QRCode(qrDiv, {
        text: url,
        width: 220,
        height: 220,
        colorDark: '#003c46',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H
      });
    }
  }

  showAgreementModal() {
    const modalHTML = `
      <div id="ec-modal-overlay" class="ec-modal-overlay">
        <div class="ec-modal-content">
          <div class="ec-modal-header">
            <h2>CONSENT, IMAGE RELEASE AND CONFIDENTIALITY AGREEMENT</h2>
            <button id="ec-modal-close" class="ec-modal-close">&times;</button>
          </div>
          
          <div class="ec-modal-body">
            <div class="ec-event-info">
              <p><strong>Event:</strong> ${this.eventData.name}</p>
              <p><strong>Location:</strong> ${this.eventData.location}</p>
              <p><strong>Date:</strong> ${this.eventData.date}</p>
            </div>
            
            <div class="ec-agreement-text">
              <h3 style="text-align: center; margin-bottom: 1rem;">Photos, Videos and Quotes</h3>
              
              <p>In consideration of being invited to and participating in a <strong>Sachi Skin</strong> event, including but not limited to <strong>live facial treatments, bookable activations, press events, creator experiences, and consumer events</strong> ("Event"), and the benefits, exposure, and experience associated with such participation, I hereby agree as follows:</p>
              
              <h4>1. Authorization to Capture Content</h4>
              <p>I authorize <strong>Sachi Skin Pte. Ltd. Singapore 199591 (UEN: 201709582M)</strong>, its affiliates, agents, contractors, and partners ("Sachi Skin") to photograph, film, record, and capture my image, likeness, voice, and quotes during the Event ("Content").</p>
              
              <h4>2. Permission to Use Content</h4>
              <p>I authorize Sachi Skin to use, store, reproduce, publish, distribute, and communicate the Content, together with identifying information such as my name, city, or social handle, for the purpose of promoting and marketing Sachi Skin, including but not limited to:</p>
              <ul>
                <li>(i) advertising, PR, and marketing materials (print, digital, POS, paid media)</li>
                <li>(ii) educational or informational materials</li>
                <li>(iii) internal brand, training, or archive materials</li>
                <li>(iv) Sachi Skin or third-party retailer websites</li>
                <li>(v) social media platforms (including Instagram, TikTok, YouTube, Facebook, X, or similar platforms)</li>
              </ul>
              
              <h4>3. No Compensation</h4>
              <p>I understand and agree that the Content may be used by Sachi Skin worldwide and in perpetuity, and that I will not receive any compensation, payment, fees, royalties, or other remuneration for any use of the Content, whether now or in the future.</p>
              
              <h4>4. Intellectual Property Rights</h4>
              <p>I agree and acknowledge that all intellectual property rights in the Content vest in Sachi Skin to the fullest extent permitted by law. I further acknowledge that Sachi Skin retains absolute discretion in relation to the use, non-use, editing, adaptation, cropping, combination, or other modification of the Content without inspection, approval. To the extent permitted by law, I irrevocably waive any moral rights or similar rights that I may have in the Content.</p>
              
              <h4>5. Confidentiality</h4>
              <p>I acknowledge that during the Event I may be exposed to <strong>non-public information</strong>, including but not limited to unreleased products, formulations, techniques, pricing, creative concepts, or brand plans. I agree not to disclose, publish, or share any such confidential information unless and until it is made public by Sachi Skin.</p>
              
              <h4>6. Governing Law and Data Protection</h4>
              <p>This agreement is governed by the laws of the <strong>Republic of Singapore</strong>, and I submit to the jurisdiction of the Singapore courts. I acknowledge and agree that my personal data (including images, video, voice, name, and social handle) will be collected, stored, and processed by Sachi Skin in accordance with applicable data protection laws, including the Singapore (PDPA).</p>
              
              <h4>7. Minors</h4>
              <p>Where the participant is under 18 years of age, this agreement must be signed by a parent or legal guardian, who consents on the participant's behalf.</p>
              
              <div style="margin-top: 2rem; padding: 1rem; background: #f8f9fa; border-radius: 8px;">
                <h4 style="margin-top: 0;">Data Protection Notice</h4>
                <p style="font-size: 0.9rem; margin-bottom: 0;">Sachi Skin will process your personal data and Content for the purposes outlined above, including sharing with trusted service providers and partners acting on our behalf. For more information on how we handle personal data and your rights, please refer to our Privacy Policy or contact us at <strong>privacy@sachiskin.com</strong>.</p>
              </div>
              
              <p style="margin-top: 2rem;"><strong>By signing below, I confirm that I have read, understood, and irrevocably consent to the use of my photos, videos, and quotes as described in this document.</strong></p>
            </div>
          </div>
          
          <div class="ec-modal-footer">
            <button id="ec-modal-cancel" class="ec-btn ec-btn-secondary">Cancel</button>
            <button id="ec-modal-agree" class="ec-btn ec-btn-primary">I Understand & Agree</button>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    
    document.getElementById('ec-modal-close')?.addEventListener('click', () => this.closeModal());
    document.getElementById('ec-modal-cancel')?.addEventListener('click', () => this.closeModal());
    document.getElementById('ec-modal-agree')?.addEventListener('click', () => {
      this.closeModal();
      this.currentStep = 'signing';
      this.render();
      this.attachEvents();
    });
    
    // Close on overlay click
    document.getElementById('ec-modal-overlay')?.addEventListener('click', (e) => {
      if (e.target.id === 'ec-modal-overlay') {
        this.closeModal();
      }
    });
  }

  closeModal() {
    document.getElementById('ec-modal-overlay')?.remove();
  }

  copyURL() {
    const input = document.getElementById('ec-consent-url');
    input.select();
    document.execCommand('copy');
    
    const btn = document.getElementById('ec-copy-btn');
    btn.textContent = 'Copied!';
    setTimeout(() => btn.textContent = 'Copy', 2000);
  }

  downloadQR() {
    const name = document.getElementById('ec-event-name')?.value || 'sachi-skin-event';
    const location = document.getElementById('ec-event-location')?.value;
    const date = document.getElementById('ec-event-date')?.value;
    
    if (!name || !location || !date) {
      alert('Please fill in all event details before downloading');
      return;
    }
    
    const canvas = document.querySelector('#ec-qr-code canvas');
    if (!canvas) {
      alert('QR code not generated yet');
      return;
    }
    
    // Create a new canvas with white background
    const newCanvas = document.createElement('canvas');
    newCanvas.width = 400;
    newCanvas.height = 400;
    const ctx = newCanvas.getContext('2d');
    
    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 400, 400);
    
    // Draw QR code
    ctx.drawImage(canvas, 0, 0, 400, 400);
    
    // Download
    const link = document.createElement('a');
    link.download = `sachi-skin-consent-qr-${name.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.href = newCanvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  initSignaturePad() {
    const canvas = document.getElementById('ec-signature-canvas');
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = 200 * ratio;
    canvas.getContext('2d').scale(ratio, ratio);

    this.signaturePad = new SignaturePad(canvas, {
      backgroundColor: '#fff',
      penColor: '#003c46'
    });

    this.signaturePad.addEventListener('endStroke', () => this.validateForm());
  }

  validateForm() {
    const name = document.getElementById('ec-full-name')?.value;
    const email = document.getElementById('ec-email')?.value;
    const agreed = document.getElementById('ec-agree')?.checked;
    const hasSig = this.signaturePad && !this.signaturePad.isEmpty();

    const valid = name && email && agreed && hasSig;
    document.getElementById('ec-btn-submit').disabled = !valid;
  }

  async submitConsent() {
    this.formData = {
      fullName: document.getElementById('ec-full-name').value,
      email: document.getElementById('ec-email').value,
      signature: this.signaturePad.toDataURL(),
      eventName: this.eventData.name,
      eventLocation: this.eventData.location,
      eventDate: this.eventData.date,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    const btn = document.getElementById('ec-btn-submit');
    btn.disabled = true;
    btn.textContent = 'Submitting...';

    try {
      console.log('Submitting consent data:', this.formData);
      
      const response = await fetch('https://ejhsttpvnyqmeziyrvjn.supabase.co/functions/v1/event-consent-handler', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVqaHN0dHB2bnlxbWV6aXlydmpuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIwNzU2OTUsImV4cCI6MjA2NzY1MTY5NX0.NjopauGkpEbnJNw8GEVG-t8zCzBHrUyNGg-bmpNwhEA'
        },
        body: JSON.stringify(this.formData)
      });

      const responseData = await response.json();
      console.log('Response:', responseData);

      if (response.ok) {
        this.currentStep = 'success';
        this.render();
      } else {
        alert(`Submission failed: ${responseData.message || 'Please try again.'}`);
        btn.disabled = false;
        btn.textContent = 'Submit Consent';
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Error submitting form. Please check your connection.');
      btn.disabled = false;
      btn.textContent = 'Submit Consent';
    }
  }
}

// Initialize
if (typeof SignaturePad !== 'undefined' && typeof QRCode !== 'undefined') {
  const app = new EventConsentApp();
  app.init();
} else {
  setTimeout(() => {
    const app = new EventConsentApp();
    app.init();
  }, 200);
}
